export function getAllPackingSlipsForPO({ po, shipment = null, shipments = [] }) {
  // Find all matching shipment documents for this PO
  const matchingShipments = shipments.filter(s => 
    (po?.id && s.poId === po.id) || 
    (po?.poNumber && s.poNumber === po.poNumber) ||
    (shipment?.id && s.id === shipment.id)
  );
  
  const primaryShipment = shipment || matchingShipments[0] || null;
  const allBatches = [];
  const batchedItemKeys = new Set();

  // 1. Collect all formal batches from shipmentsHistory across matching shipments
  matchingShipments.forEach(s => {
    (s.shipmentsHistory || []).forEach(b => {
      if (!allBatches.some(existing => existing.id === b.id || (existing.shippedAt === b.shippedAt && existing.trackingNumber === b.trackingNumber && JSON.stringify(existing.items) === JSON.stringify(b.items)))) {
        let batchCost = b.actualShippingCost;
        if (batchCost === undefined || batchCost === null) {
          const fallbackSum = (b.items || []).reduce((sum, it) => {
            const k = it.poItemId || it.id;
            const c = parseFloat(s.itemStatuses?.[k]?.actualShippingCost || 0);
            return sum + (isNaN(c) ? 0 : c);
          }, 0);
          batchCost = fallbackSum > 0 ? fallbackSum : (s.actualShippingCost || 0);
        }
        allBatches.push({
          ...b,
          actualShippingCost: parseFloat(batchCost) || 0,
          shipmentId: s.id
        });
        (b.items || []).forEach(it => {
          if (it.poItemId) batchedItemKeys.add(String(it.poItemId));
          if (it.id) batchedItemKeys.add(String(it.id));
        });
      }
    });
  });

  // 2. Check for any shipped items in po.items or matching shipments that are NOT yet recorded in allBatches
  const poItems = po?.items || [];
  const targetShipment = primaryShipment || matchingShipments[0] || {};
  const unbatchedShippedItems = [];

  poItems.forEach(item => {
    // Check if this item is already accounted for in existing batches
    const isAlreadyBatched = batchedItemKeys.has(String(item.id)) || 
      Array.from(batchedItemKeys).some(k => k === String(item.id) || k.startsWith(String(item.id) + "_"));
    
    if (!isAlreadyBatched) {
      // Find item status across target shipment or matching shipments
      let itemStatObj = targetShipment.itemStatuses?.[item.id] || 
        Object.entries(targetShipment.itemStatuses || {}).find(([k]) => k === item.id || k.startsWith(item.id + "_"))?.[1];
      
      if (!itemStatObj) {
        for (const s of matchingShipments) {
          itemStatObj = s.itemStatuses?.[item.id] || 
            Object.entries(s.itemStatuses || {}).find(([k]) => k === item.id || k.startsWith(item.id + "_"))?.[1];
          if (itemStatObj) break;
        }
      }

      const itemStatus = (typeof itemStatObj === "object" ? itemStatObj?.status : itemStatObj) || targetShipment.status || "To Order";
      if (itemStatus === "Transport -> Client" || itemStatus === "Delivered" || itemStatus === "Client") {
        unbatchedShippedItems.push({
          item,
          itemStatObj
        });
      }
    }
  });

  if (unbatchedShippedItems.length > 0) {
    // Group unbatched items by shipDate / carrier / tracking
    const dateGroups = {};
    unbatchedShippedItems.forEach(({ item, itemStatObj }) => {
      const shipDate = (typeof itemStatObj === "object" ? itemStatObj?.shipDate : null) || targetShipment.shipDate || "default";
      const groupKey = `${shipDate}_${(typeof itemStatObj === "object" ? itemStatObj?.trackingNumber : null) || targetShipment.trackingNumber || "N/A"}`;
      
      if (!dateGroups[groupKey]) {
        dateGroups[groupKey] = {
          shipDate,
          carrier: (typeof itemStatObj === "object" ? itemStatObj?.carrier : null) || targetShipment.carrier,
          trackingNumber: (typeof itemStatObj === "object" ? itemStatObj?.trackingNumber : null) || targetShipment.trackingNumber,
          destination: targetShipment.destination,
          items: []
        };
      }
      dateGroups[groupKey].items.push({
        id: item.id,
        poItemId: item.id,
        lineNumber: item.lineNumber || "",
        name: item.name || item.description || "Item",
        description: item.description || "",
        quantity: Number(item.quantity || 0)
      });
    });

    Object.keys(dateGroups).forEach((k) => {
      const g = dateGroups[k];
      let derivedCost = targetShipment.actualShippingCost;
      if (derivedCost === undefined || derivedCost === null) {
        const fallbackSum = (g.items || []).reduce((sum, it) => {
          const k = it.poItemId || it.id;
          const c = parseFloat(targetShipment.itemStatuses?.[k]?.actualShippingCost || 0);
          return sum + (isNaN(c) ? 0 : c);
        }, 0);
        derivedCost = fallbackSum > 0 ? fallbackSum : 0;
      }
      allBatches.push({
        id: `derived-batch-${allBatches.length + 1}-${Date.now()}`,
        batchNumber: allBatches.length + 1,
        shippedAt: g.shipDate !== "default" ? g.shipDate : (targetShipment.updatedAt || new Date().toISOString()),
        carrier: g.carrier || targetShipment.carrier || "N/A",
        trackingNumber: g.trackingNumber || targetShipment.trackingNumber || "N/A",
        destination: g.destination || targetShipment.destination,
        items: g.items,
        actualShippingCost: parseFloat(derivedCost) || 0,
        shipmentId: targetShipment.id
      });
    });
  }

  // Sort batches by shippedAt ascending
  allBatches.sort((a, b) => new Date(a.shippedAt || 0) - new Date(b.shippedAt || 0));

  // Re-index batchNumber sequentially
  allBatches.forEach((b, idx) => {
    b.batchNumber = idx + 1;
  });

  return {
    primaryShipment,
    batches: allBatches,
    totalBatches: allBatches.length
  };
}

export function generatePackingSlipHtml({
  shipment,
  po,
  batch = null,
  batchIndex = null,
  totalBatches = 1,
  companyLogoUrl = null
}) {
  const isBatch = !!batch;
  const slipTitle = isBatch
    ? `Packing Slip - Shipment #${batchIndex !== null ? batchIndex + 1 : (batch.batchNumber || 1)}${totalBatches > 1 ? ` of ${totalBatches}` : ""}`
    : "Packing Slip";

  const trackingNum = isBatch ? (batch.trackingNumber || "N/A") : (shipment?.trackingNumber || "N/A");
  const carrierName = isBatch ? (batch.carrier || "N/A") : (shipment?.carrier || "N/A");
  const dispatchDate = isBatch && batch.shippedAt
    ? new Date(batch.shippedAt).toLocaleDateString()
    : new Date().toLocaleDateString();

  const destination = isBatch && batch.destination
    ? batch.destination
    : (shipment?.destination || (po?.shipToAddress ? `${po.shipToAddress}${po.shipToAddress2 ? `, ${po.shipToAddress2}` : ""}, ${po.shipToCity || ""}, ${po.shipToState || ""} ${po.shipToZip || ""}` : "Client Facility"));

  const poNumber = shipment?.poNumber || po?.poNumber || "N/A";
  const customerPo = po?.customerPoNumber || shipment?.customerPoNumber || "";
  const clientName = shipment?.clientName || po?.clientName || "N/A";

  // Resolve items
  let displayItems = [];
  if (isBatch && batch.items) {
    displayItems = batch.items.map(item => {
      const matchedPoItem = po?.items?.find(pi => pi.id === (item.poItemId || item.id));
      const lineNum = item.lineNumber || matchedPoItem?.lineNumber || "";
      const displayName = item.description && item.name && item.description !== item.name && item.name !== "N/A"
        ? `${item.name} - ${item.description}`
        : (item.description || (item.name && item.name !== "N/A" ? item.name : "") || matchedPoItem?.description || "Item");
      return {
        lineNumber: lineNum || "—",
        name: displayName,
        quantity: Number(item.quantity || 0)
      };
    });
  } else {
    const rawItems = po?.items || [];
    displayItems = rawItems
      .filter(item => {
        const itemStatObj = shipment?.itemStatuses?.[item.id] ||
          Object.entries(shipment?.itemStatuses || {}).find(([k]) => k === item.id || k.startsWith(item.id + "_"))?.[1];
        const itemStatus = (typeof itemStatObj === "object" ? itemStatObj?.status : itemStatObj) || shipment?.status || "To Order";
        return itemStatus === "Transport -> Client" || itemStatus === "Delivered" || itemStatus === "Client";
      })
      .map(item => {
        const lineNum = item.lineNumber || "";
        const displayName = item.description && item.name && item.description !== item.name && item.name !== "N/A"
          ? `${item.name} - ${item.description}`
          : (item.description || (item.name && item.name !== "N/A" ? item.name : "") || "Item");
        return {
          lineNumber: lineNum || "—",
          name: displayName,
          quantity: Number(item.quantity || 0)
        };
      });
  }

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${poNumber} - ${slipTitle}</title>
        <style>
          body {
            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
            color: #2c3e50;
            padding: 40px;
            line-height: 1.5;
            background: #ffffff;
          }
          .header-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          .header-table td {
            border: none;
            padding: 0;
            vertical-align: top;
          }
          .title {
            font-size: 26px;
            font-weight: 700;
            color: #2c3e50;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .badge-partial {
            display: inline-block;
            background: #e0f2fe;
            color: #0369a1;
            font-size: 11px;
            font-weight: 700;
            padding: 3px 8px;
            border-radius: 4px;
            text-transform: uppercase;
            margin-left: 8px;
            vertical-align: middle;
            border: 1px solid #bae6fd;
          }
          .meta-info {
            margin-top: 15px;
            font-size: 14px;
            color: #555555;
            line-height: 1.7;
          }
          .meta-info strong {
            color: #111111;
          }
          .client-info {
            text-align: right;
          }
          .client-label {
            font-size: 11px;
            text-transform: uppercase;
            color: #888888;
            letter-spacing: 1px;
            display: block;
            margin-bottom: 4px;
          }
          .client-name {
            font-size: 18px;
            font-weight: 700;
            color: #111111;
          }
          .section-title {
            font-size: 12px;
            font-weight: 700;
            color: #666666;
            text-transform: uppercase;
            letter-spacing: 1px;
            border-bottom: 2px solid #cbd5e1;
            padding-bottom: 5px;
            margin-top: 25px;
            margin-bottom: 10px;
          }
          .address-box {
            margin-bottom: 25px;
            font-size: 14px;
            color: #333333;
          }
          table.items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 35px;
          }
          table.items-table th {
            background-color: #f1f5f9;
            border-bottom: 2px solid #cbd5e1;
            padding: 10px;
            font-size: 13px;
            font-weight: 700;
            color: #334155;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          table.items-table td {
            border-bottom: 1px solid #e2e8f0;
            padding: 12px 10px;
            font-size: 14px;
            color: #1e293b;
          }
          .text-center {
            text-align: center;
          }
          .signature-section {
            margin-top: 50px;
            display: flex;
            justify-content: space-between;
            gap: 40px;
          }
          .signature-box {
            border-top: 1px solid #94a3b8;
            padding-top: 10px;
            width: 45%;
            font-size: 13px;
            color: #64748b;
          }
          @media print {
            body {
              padding: 20px;
            }
          }
        </style>
      </head>
      <body>
        <table class="header-table">
          <tr>
            <td>
              <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
                ${companyLogoUrl ? `<img src="${companyLogoUrl}" style="width: 50px; height: 50px; border-radius: 6px; object-fit: contain;" />` : ""}
                <div>
                  <span class="title">${slipTitle}</span>
                  ${isBatch ? `<span class="badge-partial">Shipment #${batchIndex !== null ? batchIndex + 1 : (batch.batchNumber || 1)}</span>` : ""}
                </div>
              </div>
              <div class="meta-info">
                PO Number: <strong>${poNumber}</strong>${customerPo ? ` &bull; Cust PO: <strong>${customerPo}</strong>` : ""}<br/>
                Tracking Number: <strong>${trackingNum}</strong><br/>
                Carrier: <strong>${carrierName}</strong><br/>
                Date Dispatched: <strong>${dispatchDate}</strong>
                ${isBatch && batch.notes ? `<br/>Notes: <em>${batch.notes}</em>` : ""}
              </div>
            </td>
            <td class="client-info">
              <span class="client-label">Client Partner</span>
              <div class="client-name">${clientName}</div>
            </td>
          </tr>
        </table>
        
        <div class="section-title">Delivery Destination</div>
        <div class="address-box">
          ${destination}
        </div>
        
        <div class="section-title">Shipped Line Items</div>
        <table class="items-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 70px;">LN #</th>
              <th>Item Name / Description</th>
              <th class="text-center" style="width: 120px;">Qty Shipped</th>
            </tr>
          </thead>
          <tbody>
            ${displayItems && displayItems.length > 0 ? (
              displayItems.map(item => `
                <tr>
                  <td class="text-center"><strong>${item.lineNumber}</strong></td>
                  <td><strong>${item.name}</strong></td>
                  <td class="text-center" style="font-weight: 600;">${item.quantity}</td>
                </tr>
              `).join("")
            ) : `
              <tr>
                <td colspan="3" class="text-center" style="color: #94a3b8; padding: 20px;">No shipped line items in this delivery batch</td>
              </tr>
            `}
          </tbody>
        </table>

        <div class="signature-section">
          <div class="signature-box">
            Delivered By (Carrier / Dispatcher Signature)<br/><br/>
            Date: ________________________
          </div>
          <div class="signature-box">
            Received By (Client Authorized Signature)<br/><br/>
            Date: ________________________
          </div>
        </div>

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            }
          }
        </script>
      </body>
    </html>
  `;
}

export function printPackingSlip({
  shipment,
  po,
  batch = null,
  batchIndex = null,
  totalBatches = 1,
  companyLogoUrl = null
}) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Please allow popups to export the packing slip.");
    return;
  }

  const html = generatePackingSlipHtml({
    shipment,
    po,
    batch,
    batchIndex,
    totalBatches,
    companyLogoUrl
  });

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}
